<template>
  <router-link
    class="course-tile"
    :style="
      `background-image: url(${$getCourseBackground(
        course.id || course.objectID,
        'small'
      )}), linear-gradient(${$getGradient()}); height: ${height}px`
    "
    :to="$buildContentLink('course', course)"
  >
    <div class="course-tile__overlay"></div>
    <div class="course-tile__tile">
      <div class="course-tile__tile-top">
        <div class="course-tile__snipes-wrapper">
          <p v-if="isResumeSnipeShown" class="course-tile__snipe">
            Resume
          </p>
          <p
            v-if="course.has_sample_content && isFreeTrialUser"
            class="course-tile__snipe"
          >
            Included
          </p>
          <p v-if="course.status === 'coming soon'" class="course-tile__snipe">
            Coming Soon
          </p>
        </div>
        <div
          v-if="height >= tileSizes.medium || isDifficultyShown"
          class="course-tile__difficulty"
        >
          <Difficulty
            v-if="course.difficulty && course.difficulty.length"
            :difficulty="course.difficulty || ''"
            :alwaysVisible="isDifficultyShown"
          />
        </div>
        <p
          :class="[
            'course-tile__name',
            longCourseName(course) && 'course-tile__name--toggle',
          ]"
          v-tooltip="{
            content: course.name.length >= maxLength ? course.name : '',
          }"
        >
          {{ truncateTitle(course.name) }}
        </p>
        <p
          :class="[
            'course-tile__name--short',
            longCourseName(course) && 'course-tile__name--toggle',
          ]"
          v-tooltip="{
            content: course.name.length >= maxLength ? course.name : '',
          }"
        >
          {{ truncateTitle(course.name, 65) }}
        </p>
        <div
          v-if="showLastActivity(course) || course.duration_in_seconds"
          class="course-tile__statistics-wrapper"
        >
          <div class="course-tile__course-length">
            {{ courseDuration(course) }}
          </div>
          <span
            class="course-tile__statistics-separator"
            v-if="showLastActivity(course) && course.duration_in_seconds"
            >|</span
          >
          <div
            v-if="showLastActivity(course)"
            class="course-tile__course-last-activity"
          >
            Active {{ getTimeInDaysAgo(course) }}
          </div>
        </div>
      </div>
      <div class="course-tile__tile-bottom">
        <div class="course-tile__info-wrapper">
          <div class="course-tile__instructor-wrapper">
            <img
              v-if="height >= tileSizes.large || isInstructorPictureShown"
              class="course-tile__instructor-img"
              :src="getInstructorImage(course)"
              :alt="getInstructor(course)"
            />
            <p class="course-tile__instructor-name">
              {{ getInstructor(course) }}
            </p>
          </div>
          <div
            :style="getLearningAreaColor(course)"
            class="course-tile__vendor-wrapper"
          >
            <p class="course-tile__learning-area">
              {{ getLearningArea(course) }}
            </p>
            <p class="course-tile__vendor-name">
              {{ getVendor(course) }}
            </p>
          </div>
        </div>
        <div
          v-if="getProgress(course) !== 0"
          class="course-tile__progress-wrapper"
        >
          <div
            class="course-tile__progress-bar"
            :style="`width: ${getProgress(course)}%`"
          ></div>
        </div>
      </div>
    </div>
  </router-link>
</template>

<script>
import { defineComponent, ref, inject } from '@vue/composition-api'
import { formatDistance } from 'date-fns'
import {
  truncateText,
  getLearningAreaSnipeColor,
  getEntities,
  getInstructorImg,
  duration,
} from '@/helpers'
import Difficulty from '@/components/properties/Difficulty.vue'

export default defineComponent({
  name: 'CourseTile',
  components: { Difficulty },
  props: {
    course: {
      type: Object,
      required: true,
    },
    isFreeTrialUser: {
      type: Boolean,
      required: true,
    },
    height: {
      type: [String, Number],
      required: false,
      default: () => 276,
    },
    isResumeSnipeShown: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    isDifficultyShown: {
      type: Boolean,
      required: false,
    },
    isInstructorPictureShown: {
      type: Boolean,
      required: false,
    },
  },
  setup(props) {
    const maxLength = ref(90)
    const tileSizes = ref({
      small: 286,
      medium: 386,
      large: 490,
    })
    const getInstructor = course => getEntities.getInstructorName(course)
    const getInstructorImage = course => {
      const url =
        course?.instructors[0]?.profile_img_url ??
        'https://s3.amazonaws.com/ine-vegas/production/images/instructors/INE-Instructor.png'
      return getInstructorImg(url)
    }
    const getVendor = course => getEntities.getVendorName(course)
    const getLearningArea = course => getEntities.getLearningAreaName(course)
    const truncateTitle = (value, length = null) =>
      truncateText(value, !length ? maxLength.value : length)
    const getLearningAreaColor = course =>
      `--la: ${getLearningAreaSnipeColor(getLearningArea(course))}`
    const getProgress = course => {
      const progress = course?.user_status?.progress ?? course?.progress ?? 0
      return progress > 0 ? Math.floor(progress * 100) : 0
    }
    const getTimeInDaysAgo = course => {
      const time = course?.last_activity_date ?? ''
      return time && time !== 'None'
        ? `${formatDistance(new Date(), new Date(time))} ago`
        : ''
    }
    const courseDuration = course =>
      duration.durationFromSecs(course?.duration_in_seconds)
    const showLastActivity = course => {
      return course?.last_activity_date && course?.last_activity_date !== 'None'
    }
    const courseHasHeaderLabel = course => {
      return (
        props.isResumeSnipeShown ||
        (course.has_sample_content && props.isFreeTrialUser) ||
        course.status === 'coming soon'
      )
    }
    const courseHasProgressBar = course => {
      return getProgress(course) !== 0
    }
    const longCourseName = course => {
      return (
        courseHasHeaderLabel(course) &&
        courseHasProgressBar(course) &&
        course.name.length > 59
      )
    }
    return {
      maxLength,
      tileSizes,
      getInstructor,
      getInstructorImage,
      getVendor,
      getLearningArea,
      truncateTitle,
      getLearningAreaColor,
      getProgress,
      getTimeInDaysAgo,
      courseDuration,
      showLastActivity,
      longCourseName,
      courseHasHeaderLabel,
      courseHasProgressBar,
    }
  },
})
</script>

<style lang="scss">
.course-tile {
  $this: &;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  position: relative;
  border-radius: 10px;
  display: flex;
  width: 100%;
  z-index: 1;
  @include tablet {
    max-width: 400px;
  }
  &:hover {
    :after {
      opacity: 1;
      transition: 0.2s;
    }
    #{$this}__overlay {
      opacity: 1;
    }
    #{$this}__learning-area {
      color: var(--white);
    }
    #{$this}__vendor-name::before {
      left: -20px;
      width: 10px;
    }
    #{$this}__progress-wrapper {
      display: block;
    }
  }
  &__overlay {
    position: absolute;
    height: 100%;
    width: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    right: 0;
    top: 0;
    bottom: 0;
    left: 0;
    z-index: -1;
    opacity: 0;
    -webkit-backdrop-filter: saturate(100%) blur(6px);
    backdrop-filter: saturate(100%) blur(6px);
    transition: 0.2s all ease;
    border-radius: 10px;
  }
  &__tile {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 30px 20px 20px;
    width: 100%;
    border-radius: 10px;
    @include tablet {
      padding: 20px 20px 20px;
    }
    @include desktop {
      padding: 30px 30px 30px;
    }
  }
  &__tile-top,
  &__tile-bottom {
    z-index: 1;
  }
  &__tile-top {
    max-height: 50px;
  }

  &__tile-bottom {
    display: flex;
    flex-direction: column;
  }
  &__info-wrapper {
    display: flex;
    width: 100%;
    justify-content: space-between;
  }
  &__name,
  &__name--short {
    font-size: rem(14);
    color: var(--white);
    font-family: var(--font-book);
    line-height: 20px;
    max-width: 253px;
    margin: 0;
    @include tablet {
      font-size: rem(18);
      line-height: 24px;
    }
    &--short {
      display: none;
    }
  }
  &__name.course-tile__name--toggle {
    @include mobile {
      display: none;
    }
    @media screen and (min-width: 1390px) {
      display: block;
    }
  }
  &__name--short.course-tile__name--toggle {
    display: none;
    @include mobile {
      display: block;
    }
    @media screen and (min-width: 1390px) {
      display: none;
    }
  }

  &__statistics-wrapper {
    width: 100%;
    margin-top: 10px;
    color: var(--white);
    font-size: rem(10px);
    line-height: 12px;
    display: flex;
    @include tablet {
      font-size: rem(12);
      line-height: 14px;
    }
  }
  &__statistics-separator {
    margin: 0 5px;
  }
  &__course-length {
    text-transform: uppercase;
  }
  &__difficulty {
    margin: 0 0 15px 0;
  }
  &__instructor-wrapper {
    position: relative;
  }
  &__instructor-img {
    position: absolute;
    left: 0;
    border-radius: 100%;
    max-height: 50px;
    top: -48px;
    @include tablet {
      top: -70px;
      max-height: 75px;
    }
  }
  &__instructor-name {
    font-size: rem(10);
    line-height: 12px;
    color: var(--white);
    margin: 15px 0 0 0;
    @include tablet {
      font-size: rem(14);
      line-height: 20px;
    }
  }
  &__vendor-wrapper {
    position: relative;
  }
  &__learning-area {
    color: transparent;
    font-size: rem(14);
    line-height: 20px;
    font-weight: 400;
    position: relative;
    margin: 0;
    padding: 0;
    transition: 0.2s all ease;
    white-space: nowrap;
    width: 100%;
    &:before {
      content: '';
      position: absolute;
      opacity: 0;
      width: 10px;
      height: 10px;
      border-radius: 5px;
      background: var(--la);
    }
  }
  &__vendor-name {
    text-transform: uppercase;
    color: var(--white);
    font-size: rem(10);
    line-height: 12px;
    @include tablet {
      font-size: rem(14);
      line-height: 20px;
    }
    position: relative;
    margin: 0;
    padding: 0;
    text-align: right;
    width: 100%;
    &:before {
      position: absolute;
      top: -15px;
      left: calc(100% - 35px);
      width: 35px;
      height: 8px;
      content: '';
      background: var(--la);
      border-radius: 5px;
      transition: 0.3s all;
    }
  }
  &__progress-wrapper {
    width: 100%;
    margin-top: 10px;
    text-align: center;
    border-radius: 10px;
    padding: 1px;
    background-color: var(--white-25);
    @include tablet {
      margin-top: 20px;
    }
  }
  &__progress-bar {
    background-color: var(--white);
    height: 7px;
    border-radius: 10px;
    position: relative;
  }
  &__snipes-wrapper {
    position: absolute;
    top: 0;
    right: 0;
    @include tablet {
      position: relative;
      display: flex;
      align-items: center;
    }
  }
  &__snipe {
    background: var(--purple-gradient);
    margin: 0 0 10px auto;
    border-radius: 30px;
    color: var(--white);
    padding: 5px 8px;
    font-size: rem(10);
    box-shadow: 0px 0px 10px var(--chetwode-blue-50);
    border-radius: 0 10px 0 0;
    @include tablet {
      border-radius: 30px;
      font-size: rem(12);
    }
  }
}
</style>
